import { Col, Pagination, Popover, Row, Space, Spin, Table, Tag, Tooltip } from 'antd';
import React, { useState } from 'react';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    NodeIndexOutlined,
    QuestionCircleOutlined,
    WechatOutlined,
} from '@ant-design/icons';
import { useSortedRegisterTaxiDriverQuery } from '../hooks/useSortedRegisterTaxiDriverQuery';
import { useCurrentRegisterTaxiDriver } from '../hooks/useCurrentRegisterTaxiDriver';
import RegisterTaxiDriverViewModal from './RegisterTaxiDriverViewModal';
import { RegisterTaxiDriverFilter } from './RegisterTaxiDriverFilter';
import { TaxiDriver } from '../../../shared/types';
import RegisterTaxiDriverEditModal from './RegisterTaxiDriverEditModal';
import moment from 'moment-timezone';
import { useMutation } from '@apollo/client';
import DriverDetailsDrawer from './Drawer/DriverDetailsDrawer';
import { useGetDriverContractQuery } from '../../../GraphQL/hooks/useGetDriverContractQuery';
import { CITRIN_DELETE_TAXI_DRIVER, DeleteTaxiDriverRequest, DeleteTaxiDriverResponse } from '../../../GraphQL';
import PopConfirm from '../../../shared/Components/PopConfirm';
import { convertDateToFormat } from '../../../shared/Utils';

const RegisterTaxiDriverTable = () => {
    const {
        sortedRegisterTaxiDrivers: taxiDrivers,
        refetch,
        loading,
        ...pagination
    } = useSortedRegisterTaxiDriverQuery();
    const [deleteMutation] = useMutation<DeleteTaxiDriverResponse, DeleteTaxiDriverRequest>(CITRIN_DELETE_TAXI_DRIVER);
    const [isViewModalVisible, setViewModalVisible] = useState(false);
    const [isEditModalVisible, setEditModalVisible] = useState(false);
    const { setCurrentRegisterTaxiDriver } = useCurrentRegisterTaxiDriver();
    const [isDrawerVisible, setDrawerVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { loadAndDownloadPDF, loading: pdfLoading } = useGetDriverContractQuery();

    const handleViewDriverTrips = (registerTaxiDriver) => {
        setCurrentRegisterTaxiDriver(registerTaxiDriver);
        setDrawerVisible(true);
    };

    const handleEditTaxiDriver = (registerTaxiDriver: TaxiDriver) => {
        setCurrentRegisterTaxiDriver(registerTaxiDriver);
        setEditModalVisible(true);
    };

    const handleViewRegisterTaxiDriver = (registerTaxiDriver: TaxiDriver) => {
        setCurrentRegisterTaxiDriver(registerTaxiDriver);
        setViewModalVisible(true);
    };

    const handleRemoveTaxiDriver = (taxiDriver: TaxiDriver) => {
        setIsLoading(true);
        deleteMutation({ variables: { id: taxiDriver._id } })
            .then(() => {
                refetch();
            })
            .catch((err) => console.error(err))
            .finally(() => {
                setIsLoading(false);
            });
    };

    const columns = [
        {
            title: 'Full Name',
            dataIndex: 'fullName',
            key: 'fullName',
            width: 160,
        },
        {
            title: 'E-mail',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Phone',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            width: 140,
        },
        {
            title: 'Marque/Model',
            dataIndex: 'marque',
            key: 'marque',
            width: 150,
            render: (text, record) => {
                return `${record.marque} ${record.model}`;
            },
        },
        {
            title: 'Register Type',
            dataIndex: 'signUpType',
            key: 'signUpType',
            width: 130,
        },
        {
            title: 'AppVersion',
            dataIndex: 'mobileAppVersion',
            key: 'mobileAppVersion',
        },
        {
            title: 'Register Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 150,
            sorter: (a, b) => a.createdDate - b.createdDate,
            render: (text, record) => {
                return convertDateToFormat(record.createdDate, 'DD-MM-YYYY, HH:mm');
            },
        },
        {
            title: 'Register Status',
            dataIndex: 'registerStatus',
            key: 'type',
            width: 140,
            render: (status: string) => {
                let color = '';
                switch (status) {
                    case 'Pending':
                        color = '#ff0d0dff';
                        break;
                    case 'In progress':
                        color = '#FFA500';
                        break;
                    case 'Confirmed':
                        color = '#50C878';
                        break;
                }
                return <Tag color={color}>{status}</Tag>;
            },
        },
        {
            title: 'Account Status',
            dataIndex: 'accountStatus',
            key: 'accountStatus',
            width: 140,
            render: (status: string) => {
                return status === 'Active' ? (
                    <span style={{ fontWeight: 'bold', color: '#389e0d' }}>
                        <CheckCircleOutlined /> Active
                    </span>
                ) : (
                    <span style={{ color: '#8c8c8c' }}>
                        <CloseCircleOutlined /> Inactive
                    </span>
                );
            },
        },
        {
            title: 'Expired On',
            dataIndex: 'expiredAccountDate',
            key: 'expiredAccountDate',
            width: 140,
            render: (text, record) => {
                const expiredDate = moment(Number(record.expiredAccountDate));
                const isExpired = expiredDate.isBefore(moment());
                const isNearExpiration = expiredDate.diff(moment(), 'days') <= 7 && !isExpired;

                return (
                    <span>
                        {convertDateToFormat(record.expiredAccountDate, 'DD-MM-YYYY')}

                        {isExpired && (
                            <Tooltip title="Account expired">
                                <ExclamationCircleOutlined style={{ color: 'red', marginLeft: 8 }} />
                            </Tooltip>
                        )}

                        {isNearExpiration && (
                            <Tooltip title="Account will expire soon">
                                <ExclamationCircleOutlined style={{ color: '#faad14', marginLeft: 8 }} />
                            </Tooltip>
                        )}
                    </span>
                );
            },
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, registerTaxiDriver) => (
                <Space size="middle">
                    <span style={{ width: 16, display: 'inline-flex' }}>
                        {registerTaxiDriver.note && (
                            <Popover
                                content={
                                    <div
                                        style={{
                                            maxWidth: 300,
                                            whiteSpace: 'pre-wrap',
                                            padding: 10,
                                            borderRadius: 8,
                                        }}
                                    >
                                        {registerTaxiDriver.note}
                                    </div>
                                }
                                title="Note"
                                trigger="hover"
                            >
                                <WechatOutlined
                                    style={{
                                        fontSize: '16px',
                                        color: '#1553fc',
                                        cursor: 'pointer',
                                    }}
                                />
                            </Popover>
                        )}
                    </span>

                    <a onClick={() => loadAndDownloadPDF(registerTaxiDriver._id)}>
                        <DownloadOutlined style={{ fontSize: '16px', color: '#e418ff' }} />
                    </a>
                    <a href="#" onClick={() => handleViewDriverTrips(registerTaxiDriver)} style={{ color: '#1890ff' }}>
                        <NodeIndexOutlined />
                    </a>
                    <a href="#" onClick={() => handleViewRegisterTaxiDriver(registerTaxiDriver)}>
                        <EyeOutlined />
                    </a>
                    <a href="#" onClick={() => handleEditTaxiDriver(registerTaxiDriver)}>
                        <EditOutlined style={{ fontSize: '16px', color: 'green' }} />
                    </a>
                    <PopConfirm
                        title={'Delete the Taxi Driver'}
                        description={'Are you sure to delete this Taxi Driver?'}
                        onConfirmTask={() => handleRemoveTaxiDriver(registerTaxiDriver)}
                        popIcon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                        actionIcon={<DeleteOutlined style={{ fontSize: '16px', color: 'red' }} />}
                    />
                </Space>
            ),
            width: 160,
        },
    ];

    const Paginator = () => (
        <Row justify={'center'}>
            <Col>
                <Pagination
                    size="default"
                    total={pagination.totalCount}
                    pageSize={pagination.pageSize}
                    current={pagination.page}
                    showTotal={(total) => `Total Taxi Drivers ${total}`}
                    onChange={(newPage) => pagination.setPage(newPage)}
                    showQuickJumper={true}
                    showSizeChanger={false}
                    showLessItems={false}
                />
            </Col>
        </Row>
    );

    return (
        <>
            {isEditModalVisible && (
                <RegisterTaxiDriverEditModal
                    updateList={refetch}
                    isOpen={isEditModalVisible}
                    onRequestClose={setEditModalVisible}
                />
            )}
            {isViewModalVisible && (
                <RegisterTaxiDriverViewModal isOpen={isViewModalVisible} onRequestClose={setViewModalVisible} />
            )}
            {isDrawerVisible && (
                <DriverDetailsDrawer
                    open={isDrawerVisible}
                    onClose={() => setDrawerVisible(false)}
                    updateList={refetch}
                />
            )}

            <Row gutter={[16, 6]} style={{ paddingBottom: 10, paddingTop: 20 }}>
                <RegisterTaxiDriverFilter />
            </Row>

            <Row gutter={16} style={{ paddingBottom: 10, paddingTop: 10 }}>
                <Col xs={24}>
                    <Spin spinning={isLoading || loading || pdfLoading}>
                        <Table
                            dataSource={taxiDrivers}
                            columns={columns}
                            pagination={false}
                            rowKey={() => Math.random().toString()}
                            scroll={{ x: 1500 }}
                        ></Table>
                    </Spin>
                </Col>
            </Row>
            <Paginator />
        </>
    );
};

export default RegisterTaxiDriverTable;
